import { useEffect, useState } from 'react';
import { platformAnalyticsAPI, type AccountUsageScore, type UsageStatus } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Gauge, Loader2, TrendingDown, TrendingUp } from 'lucide-react';

const STATUS: Record<UsageStatus, { label: string; className: string }> = {
  healthy: { label: 'Healthy', className: 'bg-emerald-100 text-emerald-800' },
  watch: { label: 'Watch', className: 'bg-amber-100 text-amber-800' },
  at_risk: { label: 'At risk', className: 'bg-red-100 text-red-800' },
};

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

/**
 * Account usage score (FY27 objective 6.2): one 0-100 number for this
 * install over the last 30 days, the 30 days before it, and the three
 * components it is built from. Real use only — background polling,
 * Valuechain staff and Login-As are left out.
 */
export default function AccountUsageScoreCard() {
  const [data, setData] = useState<AccountUsageScore | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    platformAnalyticsAPI.getUsageScore()
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err: unknown) => {
        console.error('Usage score fetch failed:', err);
        if (!cancelled) setError('Could not load the account usage score.');
      });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <Card><CardContent className="py-6 flex items-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-4 w-4" />{error}
      </CardContent></Card>
    );
  }
  if (!data) {
    return (
      <Card><CardContent className="py-6 flex justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </CardContent></Card>
    );
  }

  const { current: cur, previous: prev, weights: w, thresholds: t } = data;
  const c = cur.components;
  const i = cur.inputs;
  const status = STATUS[data.status];
  if (i.customer_logins === 0) {
    return (
      <Card><CardContent className="py-6 text-sm text-muted-foreground">
        No customer logins on this install yet, so there is no usage to score.
      </CardContent></Card>
    );
  }
  const up = data.change > 0;
  const down = data.change < 0;
  const parts = [
    {
      label: 'Frequency', weight: pct(w.frequency), value: c.frequency,
      detail: `customer people active ${i.customer_active_days_per_person} of ${i.working_days} working days`,
    },
    {
      label: 'Reach', weight: pct(w.reach), value: c.reach,
      detail: `customer team ${i.customer_people_active}/${i.customer_logins} people (${pct(c.customer_reach)}) · suppliers ${i.supplier_companies_active}/${i.supplier_companies_with_login} companies (${pct(c.supplier_reach)})`,
    },
    {
      label: 'Breadth', weight: pct(w.breadth), value: c.breadth,
      detail: `${i.work_areas_used} of ${i.work_areas_live} work areas in use on this install`,
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2"><Gauge className="h-5 w-5" /> Account usage score</CardTitle>
        <p className="text-xs text-muted-foreground">
          Last 30 days ({cur.date_from} to {cur.date_to}) vs the 30 days before. Real use only:
          background polling, Valuechain staff and Login-As are left out.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-start gap-8">
          <div>
            <p className="text-5xl font-semibold tabular-nums">{cur.score}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
              <span className={`text-xs flex items-center gap-1 ${up ? 'text-emerald-700' : down ? 'text-red-700' : 'text-muted-foreground'}`}>
                {up && <TrendingUp className="h-3 w-3" />}
                {down && <TrendingDown className="h-3 w-3" />}
                {up ? '+' : ''}{data.change} vs {prev.score} before
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-[280px] space-y-3">
            {parts.map((p) => (
              <div key={p.label}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{p.label} <span className="text-muted-foreground font-normal">· weight {p.weight}</span></span>
                  <span className="tabular-nums">{pct(p.value)}</span>
                </div>
                <div className="h-2 bg-muted rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.max(p.value * 100, 1)}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{p.detail}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          {t.healthy}+ healthy · {t.watch}–{t.healthy - 1} watch · under {t.watch} at risk · a fall of {t.drop_alert} points or more is at risk.
        </p>
      </CardContent>
    </Card>
  );
}
